import * as ExcelJS from 'exceljs';
import {defaultStyle} from './default-style';
import {pixelToPtHeight, pixelToPtWidth, ptHeightToPixel} from './pixel-to-pt';
import {s3GetCognito} from 'utils/cognito';

/**
 * 기본 페이지 설정
 * @param pageSetup - 페이지 설정 옵션
 * @param margins - 마진 설정 옵션
 * @returns 페이지 설정 객체
 * @public
 */
export function defaultPageSetup(
  pageSetup?: {
    paperSize?: number;
    fitToPage?: boolean;
    fitToHeight?: number;
    fitToWidth?: number;
  },
  margins?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
    header?: number;
    footer?: number;
  },
) {
  return {
    pageSetup: {
      paperSize: pageSetup && pageSetup.paperSize ? pageSetup.paperSize : 9,
      fitToPage: pageSetup && pageSetup.fitToPage ? pageSetup.fitToPage : true,
      fitToHeight:
        pageSetup && pageSetup.fitToHeight ? pageSetup.fitToHeight : 3,
      fitToWidth: pageSetup && pageSetup.fitToWidth ? pageSetup.fitToWidth : 1,
      margins: {
        top: margins && margins.top ? margins.top : 0.3,
        bottom: margins && margins.bottom ? margins.bottom : 0.3,
        left: margins && margins.left ? margins.left : 0.1,
        right: margins && margins.right ? margins.right : 0.1,
        header: margins && margins.header ? margins.header : 0,
        footer: margins && margins.footer ? margins.footer : 0,
      },
      horizontalCentered: true,
    },
    views: [{}],
  };
}

/**
 * 기본 스타일 설정
 * @param ws - 워크시트
 * @param cell - 셀
 * @param height - 높이 (기본값: 40)
 * @param width - 너비 (기본값: 20)
 * @public
 */
export function defaultStyleSetup(
  ws: any,
  cell: ExcelJS.Cell,
  height = 40,
  width = 20,
) {
  ws.getCell(cell.address).style = defaultStyle;
  ws.getRow(cell.fullAddress.row).height = pixelToPtHeight(height);
  ws.getColumn(cell.fullAddress.col).width = pixelToPtWidth(width);
}

/**
 * url을 base64로 변환하고, 그 이미지의 width height 알아내기
 * @param url - 이미지 URL
 * @returns base64 문자열과 이미지 크기 정보 또는 null
 * @public
 */
export const imgBase64AndSize = async (
  url: any,
): Promise<null | {
  urlToBase64: string;
  imageSize: {width: number; height: number};
}> => {
  const imageDimensions = (
    imageBase64: string,
  ): Promise<{width: number; height: number} | false> =>
    new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const {width, height} = img;
        resolve({width, height});
      };

      img.onerror = () => {
        reject('imgBase64AndSize 실패');
      };

      img.src = imageBase64;
    });

  return null;
};

/**
 * 엑셀 셀 크기에 따른 이미지 리사이징
 * @param imageWidth - 이미지 너비
 * @param imageHeight - 이미지 높이
 * @param totalColNum - 전체 컬럼 수
 * @param rowHeight - 행 높이
 * @returns 리사이징된 이미지 크기 및 오프셋 정보
 * @public
 */
export function calculateImageSize(
  imageWidth: number,
  imageHeight: number,
  totalColNum: number,
  rowHeight: number,
) {
  const containerWidth = 2.5 * 9.6 * totalColNum;
  const containerHeight = ptHeightToPixel(rowHeight);
  // 이미지의 비율 계산
  const imageRatio = imageWidth / imageHeight;
  // 이미지의 가로, 세로 중앙 정렬
  let offsetX = 0;
  let offsetY = 0;

  // 박스의 너비에 맞추어 이미지의 너비 조정
  let newWidth = containerWidth;
  let newHeight = containerWidth / imageRatio;

  // 이미지의 높이가 박스의 높이보다 큰 경우
  if (newHeight > containerHeight) {
    newHeight = containerHeight * 0.9;
    newWidth = containerHeight * imageRatio * 0.9;
    // 좌우 정렬

    offsetX = Number((containerWidth / 2 - newWidth / 2) / (2.5 * 9.6));
    offsetY =
      Number((containerHeight / 2 - newHeight / 2) / containerHeight) || 0;
  } else {
    // 상하 정렬
    newHeight = newHeight * 0.9;
    newWidth = newWidth * 0.9;
    offsetX = Number((containerWidth / 2 - newWidth / 2) / (2.5 * 9.6));
    offsetY =
      Number((containerHeight / 2 - newHeight / 2) / containerHeight) || 0;
  }

  return {
    width: Math.floor(newWidth),
    height: Math.floor(newHeight),
    offsetX: Math.floor(offsetX) + 0.9,
    offsetY,
  };
}

/**
 * 이미지 셀 생성
 * @param wb - 워크북
 * @param ws - 워크시트
 * @param cellInfo - 셀 정보
 * @param tmpProps - 임시 속성
 * @public
 */
export async function makeImgCell(
  wb: ExcelJS.Workbook,
  ws: ExcelJS.Worksheet,
  cellInfo: {
    img?: string;
    [key: string]: any;
  },
  tmpProps?: any,
) {
  const startCell = ws.getCell(returnFirstCell(cellInfo.mergeRange));
  const endCell = ws.getCell(cellInfo.mergeRange.split(':')[1]);
  const firstCellRow = ws.getCell(returnFirstCell(cellInfo.mergeRange))
    .fullAddress.row;
  const firstCellCol = ws.getCell(returnFirstCell(cellInfo.mergeRange))
    .fullAddress.col;
  const firstCellColWidth = ws.getColumn(firstCellCol).width;
  startCell.style = cellInfo.style || {border: {...defaultStyle.border}};

  ws.mergeCells(cellInfo.mergeRange);

  // rowHeight가 있다면 높이설정
  if (cellInfo.rowHeight) {
    const cellRow = ws.getCell(returnFirstCell(cellInfo.mergeRange)).fullAddress
      .row;
    ws.getRow(cellRow).height = cellInfo.rowHeight;
  }

  let cognitoPayload = {
    imgUrls: [cellInfo.img],
    ...tmpProps,
  };

  if (cellInfo.cognitoData) {
    cognitoPayload.uuid = cellInfo.cognitoData.uuid;
    cognitoPayload.type = cellInfo.cognitoData.type;
  }

  if (cellInfo.img) {
    let img = [''];
    if (cellInfo.completeConvert) {
      img = [cellInfo.img];
    } else {
      const s3Img: any = await s3GetCognito(cognitoPayload);
      if (s3Img[0]) img = [s3Img[0]];
    }

    if (img[0] !== '') {
      const imageInfo = await imgBase64AndSize(img[0]);

      if (imageInfo) {
        const type = imageInfo.urlToBase64.toString().split('base64,')[0];
        const base64 = imageInfo.urlToBase64.toString().split('base64,')[1];

        const imageId = wb.addImage({
          base64,
          extension: type.includes('png') ? 'png' : 'jpeg',
        });

        const {width, height, offsetX, offsetY} = calculateImageSize(
          imageInfo.imageSize.width,
          imageInfo.imageSize.height,
          endCell.fullAddress.col - startCell.fullAddress.col + 1,
          cellInfo.rowHeight,
        );

        ws.addImage(imageId, {
          tl: {
            col: firstCellCol - 1 + offsetX,
            row: firstCellRow - 1 + offsetY,
          } as ExcelJS.Anchor,
          ext: {
            width,
            height,
          },
        });
      }
    }
  }
}

/**
 * 셀 생성
 * @param ws - 워크시트
 * @param cellInfo - 셀 정보
 * @param defaultStyle - 기본 스타일
 * @public
 */
export function makeCell(
  ws: ExcelJS.Worksheet,
  cellInfo: any,
  defaultStyle: Partial<ExcelJS.Style>,
) {
  const firstCellAddress = returnFirstCell(cellInfo.mergeRange);
  const cell = ws.getCell(firstCellAddress);

  cell.value = cellInfo.cellValue;
  cell.style = {
    ...defaultStyle,
    fill: {
      type: 'pattern',
      pattern: 'solid',
      fgColor: {argb: cellInfo.fillColor},
    },
    font: {
      ...defaultStyle.font,
      size: cellInfo.fontSize ? cellInfo.fontSize : defaultStyle?.font?.size,
      color: {
        argb: cellInfo.fontColor
          ? cellInfo.fontColor
          : defaultStyle?.font?.color?.argb,
      },
      bold: cellInfo.fontBold ? cellInfo.fontBold : defaultStyle?.font?.bold,
    },
    alignment: cellInfo.alignment ? cellInfo.alignment : defaultStyle.alignment,
    border: cellInfo.border ? {...cellInfo.border} : {...defaultStyle.border},
  };

  // 병합된 셀 범위에 스타일 적용
  ws.mergeCells(cellInfo.mergeRange);

  // rowHeight가 있다면 높이설정
  if (cellInfo.rowHeight) {
    const cellRow = cell.fullAddress.row;
    ws.getRow(cellRow).height = cellInfo.rowHeight;
  }
}

/**
 * 병합 범위에서 첫 번째 셀 주소 반환
 * @param mergeRange - 병합 범위 (예: 'A1:B2')
 * @returns 첫 번째 셀 주소 (예: 'A1')
 * @public
 */
export function returnFirstCell(mergeRange: string) {
  return mergeRange.split(':')[0];
}
